import { useQuery } from "@tanstack/react-query";
import { BosDurum, Iskelet, Kart, Rozet } from "@koclukpanel/ui";
import { degisiklikTalepleri, talepleriGetir } from "../api";
import { TALEP_DURUM_ADI } from "../Talepler";
import { DURUM_ADI, DURUM_TONU, NEDEN_ADI } from "../DegisiklikTalepleri";
import type { SekmeOzellikleri } from "./sekmeler";

/**
 * TALEPLER SEKMESİ — F-PPL-04
 *
 * ================================================================
 * BİR SEKME, İKİ AYRI TALEP TÜRÜ — VE İKİSİ AYNI ŞEY DEĞİL
 * ================================================================
 * Öğrenciden gelen talepler iki ayrı tabloda yaşıyor:
 *
 *   `StudentRequest`          -> genel talep (dondurma, fatura, şikâyet…)
 *                                kategori taşıyor, karar izni KATEGORİDEN
 *   `ScheduleChangeRequest`   -> haftalık program değişikliği
 *                                aylık HAK sayacına bağlı (F-SCH-12)
 *
 * Tek listede birleştirmek cazipti ama ikisinin karar kuralı, izni ve
 * sayacı farklı. Birleştirilmiş bir liste, "kaç değişiklik hakkı
 * kaldı" sorusunu cevaplayamaz hâle gelirdi.
 *
 * ================================================================
 * ÇEVİRİ TABLOLARI BURADA YENİDEN YAZILMIYOR — VE BU ÖLÇÜLDÜ
 * ================================================================
 * İlk yazımda durum ve sebep adlarını bu dosyada yeniden yazmıştım.
 * Canlı denemede iki hata birden çıktı:
 *
 *   · `StudentRequest`in `IN_REVIEW` durumunu ATLAMIŞTIM,
 *   · `ScheduleChangeRequest`in `ACCEPTED` / `PARTIALLY_ACCEPTED`
 *     durumları tablomda hiç yoktu — ekranda ham enum çıkıyordu,
 *   · sebep alanı `SCHOOL_LOAD` diye görünüyordu.
 *
 * Üçünün de çevirisi zaten VARDI, başka iki ekranda. Tablolar oradan
 * dışa aktarıldı ve buraya alındı: iki enum, iki tablo, tek ev.
 *
 * ⚠️ Buradaki izin süzgeci bir KORUMA DEĞİL. Sunucu tarafı ölçüldü
 * (05.09.2026): `operasyon.service.ts` -> `talepListesi` ve
 * `degisiklik.service.ts` -> `listele`, ikisi de `ogrenciKapsamFiltresi`
 * uyguluyor ve `ogrenciId` süzgeci kapsamın YANINA `AND` ile
 * ekleniyor. Koruma orada.
 */

function gun(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR");
}

/**
 * `quotaMonth` bir AY, bir gün değil (`@db.Date`, ayın ilki).
 *
 * Ham hâli ekranda `2026-09-01T00:00:00.000Z` diye çıkıyordu — canlı
 * denemede görüldü. Gün göstermek de yanıltıcı olurdu: hak sayacı
 * aylık, "1 Eylül" diye bir hak yok.
 */
function ay(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
}

export function TaleplerSekmesi({ ogrenciId, izinVar }: SekmeOzellikleri) {
  const genelGorebilir =
    izinVar("operasyon.talep.olustur") ||
    izinVar("operasyon.talep.karara_bagla") ||
    izinVar("finans.talep.karara_bagla");

  const genel = useQuery({
    queryKey: ["ogrenci-talepleri", ogrenciId],
    queryFn: () => talepleriGetir(undefined, ogrenciId),
    retry: false,
    enabled: genelGorebilir,
  });

  const program = useQuery({
    queryKey: ["ogrenci-degisiklik-talepleri", ogrenciId],
    queryFn: () => degisiklikTalepleri(undefined, ogrenciId),
    retry: false,
    enabled: izinVar("program.degisiklik.goruntule"),
  });

  return (
    <div className="flex flex-col gap-4">
      {genelGorebilir && (
        <Kart baslik="Talepler" aciklama="Öğrencinin açtığı talepler — F-REQ">
          {genel.isPending ? (
            <Iskelet satir={3} />
          ) : genel.isError ? (
            <p className="text-hata-700 text-sm">Talepler alınamadı.</p>
          ) : genel.data.talepler.length === 0 ? (
            <p className="text-notr-600 text-sm">Bu öğrencinin talebi yok.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {genel.data.talepler.map((t) => (
                <li key={t.id} className="border-notr-200 flex flex-col gap-1 border-b pb-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-notr-900 text-sm font-medium">{t.subject}</span>
                    {/*
                      Katalogda olmayan bir durum SAKLANMIYOR: ham degeri
                      yazmak, "durum yok" sanmaktan iyi. Sunucu yeni bir
                      durum eklerse ekran sessizce bos kalmasin.
                    */}
                    <Rozet>{TALEP_DURUM_ADI[t.status] ?? t.status}</Rozet>
                    <span className="text-notr-600 text-xs">{t.category.name}</span>
                  </div>
                  <p className="text-notr-700 line-clamp-2 text-sm">{t.body}</p>
                  <p className="text-notr-600 text-xs">
                    {gun(t.createdAt)}
                    {t.decidedAt !== null && ` · karar: ${gun(t.decidedAt)}`}
                  </p>
                  {t.decisionNote !== null && (
                    <p className="text-notr-700 text-xs italic">{t.decisionNote}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Kart>
      )}

      {izinVar("program.degisiklik.goruntule") && (
        <Kart
          baslik="Program değişiklik talepleri"
          aciklama="Aylık değişiklik hakkına bağlı" /* F-SCH-12 */
        >
          {program.isPending ? (
            <Iskelet satir={3} />
          ) : program.isError ? (
            <p className="text-hata-700 text-sm">Değişiklik talepleri alınamadı.</p>
          ) : program.data.talepler.length === 0 ? (
            <p className="text-notr-600 text-sm">Bu öğrencinin program değişikliği talebi yok.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {program.data.talepler.map((t) => (
                <li key={t.id} className="border-notr-200 flex flex-col gap-1 border-b pb-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-notr-900 text-sm font-medium">
                      {NEDEN_ADI[t.reason] ?? t.reason}
                    </span>
                    <Rozet ton={DURUM_TONU[t.status] ?? "notr"}>
                      {DURUM_ADI[t.status] ?? t.status}
                    </Rozet>
                    {/*
                      ================================================================
                      `grantIndex` GÖSTERİLMİYOR — 0 TABANLI BİR DİZİN
                      ================================================================
                      İlk yazımda `{grantIndex}. hak` yazıyordum ve ekranda
                      "0. hak" çıkıyordu. `DegisiklikTalepleri.tsx` bu tuzağı
                      ZATEN yazmıştı ("0 tabanlı bir dizin, kullanıcıya hiçbir
                      şey anlatmıyor") — o notu okumadan aynı hatayı yeniden
                      yaptım.

                      Anlamı taşıyan tek bilgi hakkın AYLIK mı EK mi olduğu.
                    */}
                    <span className="text-notr-600 text-xs">
                      {ay(t.quotaMonth)} · {t.isExtraGrant ? "ek hak" : "aylık hak"}
                    </span>
                  </div>
                  {t.reasonText.length > 0 && (
                    <p className="text-notr-700 line-clamp-2 text-sm">{t.reasonText}</p>
                  )}
                  <p className="text-notr-600 text-xs">
                    {gun(t.createdAt)}
                    {t.decidedAt !== null && ` · karar: ${gun(t.decidedAt)}`}
                  </p>
                  {t.coachResponse !== null && (
                    <p className="text-notr-700 text-xs italic">{t.coachResponse}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Kart>
      )}

      {/*
        Sekme dort izinden BIRI icin aciliyor; hicbiri yoksa buraya hic
        gelinmiyor. Yine de sessiz bos bir panel birakilmiyor.
      */}
      {!genelGorebilir && !izinVar("program.degisiklik.goruntule") && (
        <BosDurum
          baslik="Görüntülenecek talep türü yok"
          aciklama="Bu öğrencinin talepleri için yetkiniz bulunmuyor."
        />
      )}
    </div>
  );
}
